/**
 * The browser half of docs/09 § 62 — Turnstile on the OTP request.
 *
 * The Worker is where the control lives: `apps/api/src/auth/turnstile.ts` POSTs the
 * token to `challenges.cloudflare.com/turnstile/v0/siteverify` with the secret and
 * refuses the request unless Cloudflare validates it. This file's only job is to make
 * a token exist for an honest client to send. A token nobody verifies is a string the
 * client chose, so nothing here is a security control on its own, and none of it can
 * be trusted from the server's side.
 *
 * ── HOW IT IS RENDERED, AND WHY THAT IS A DESIGN DECISION AND NOT A DEFAULT ──
 *
 * `Oprappo P0 Screens.dc.html` A3 draws a phone-number screen with no widget on it:
 * a back arrow, a heading, a label, a country box and a field, a hint, the terms
 * sentence, and the button. There is no box drawn for a challenge, no space reserved
 * for one, and the visual gate measures the built route against that frame at 360×680.
 * A Turnstile widget in its default appearance renders a ~300×65 iframe, which would
 * move every pixel below it and fail A3.
 *
 * So the widget is rendered with `appearance: 'interaction-only'`, which is a real
 * Turnstile mode and not a way of hiding it: the widget stays invisible and issues its
 * token silently for the large majority of visitors, and it BECOMES VISIBLE for the
 * minority Cloudflare decides to challenge. That is the whole judgement, and it is
 * worth stating both halves plainly:
 *
 *   · It satisfies § 62. The token is produced by a real widget with a real sitekey
 *     and is verified server-side; nothing about the check is weakened by the
 *     appearance setting, which governs when the iframe is DRAWN, not whether the
 *     challenge runs.
 *   · It does not make the design's silence go away. When Cloudflare does demand an
 *     interaction, a box appears on a screen that draws none — and that state is not
 *     designed. It is placed directly above the terms sentence, where the frame has a
 *     flexible spacer that absorbs its height so the button never leaves the screen,
 *     and the container is `empty:hidden` so it occupies nothing at all in the normal
 *     case. That is the least-wrong placement available, not a drawn one. REPORTED:
 *     A3 needs a drawn state for "the challenge wants an interaction", and the copy
 *     that goes with it, if any, has to be written by a native speaker.
 *
 * ── NO SITEKEY MEANS NO WIDGET, AND THAT IS DELIBERATE ───────────────────────
 *
 * `VITE_TURNSTILE_SITE_KEY` is bound at build time from the repo variable
 * `TURNSTILE_SITE_KEY` (`.github/workflows/deploy.yml`). It is `required: false` in
 * `infra/lib/env.mjs`, and a build without it — a local `vite build`, or the visual
 * gate's — must not load a third-party script or leave a rendered iframe in the
 * output. So the absent key is the switch that turns this whole module off, and it
 * mirrors the Worker's switch exactly: there, the absence of the SECRET means the
 * challenge is not enforced (`auth/turnstile.ts` § the outage decision). Bind the
 * sitekey first, the secret second, and there is never a window in which the server
 * demands a token the client cannot produce.
 *
 * ── A TOKEN IS SPENT ONCE, AND THE BUTTON WAITS FOR ONE (W2-12) ─────────────
 *
 * A3, A4, A7 and the recovery form all send the token now. Cloudflare validates a token
 * ONCE — a second siteverify of the same string answers `timeout-or-duplicate` — so a
 * screen that pressed its button twice with the token it read on the first press was
 * refused on the second every time, with the "reload the page" sentence. The token is
 * therefore TAKEN (`takeTurnstileToken`), not read: taking it clears it and asks the
 * widget for a fresh one, which arrives in about a second.
 *
 * And when a sitekey IS bound, the send button is disabled until a token exists
 * (`useTurnstile`). The Worker refuses a request with no token whenever the control is
 * deployed, so a button that could be pressed before the widget answered was a button
 * that spent a press on a certain refusal. With no sitekey the button is never held:
 * nothing will ever arrive to release it, and the Worker does not ask for a token it has
 * no secret to verify.
 */

import { useEffect, useState, type RefObject } from 'react';

/** The Turnstile API, narrowed to the four calls this file makes. */
interface TurnstileApi {
  render(
    element: HTMLElement,
    options: {
      sitekey: string;
      appearance?: 'always' | 'execute' | 'interaction-only';
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      'refresh-expired'?: 'auto' | 'manual' | 'never';
    },
  ): string;
  remove(widgetId: string): void;
  reset(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    /** The name `api.js?onload=` calls once the script is ready. */
    __oprappoTurnstileReady?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile';
const READY_CALLBACK = '__oprappoTurnstileReady';
const SCRIPT_SRC = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${READY_CALLBACK}`;

/**
 * The current token, or null.
 *
 * Module state rather than React state on purpose: the value is not rendered, and a
 * re-render caused by a token arriving would be a re-render for something invisible.
 * It is cleared on expiry, so a caller reading `null` is reading "there is no valid
 * token right now" and not "there was one once".
 */
let token: string | null = null;

/** Who is waiting to hear that the token changed. See `useTurnstile`. */
const listeners = new Set<(next: string | null) => void>();

/** The widget currently on screen, so a spent token can be replaced. One at a time. */
let liveWidget: string | null = null;

function setToken(next: string | null): void {
  token = next;
  for (const listener of listeners) listener(next);
}

/**
 * Hear about every change to the token. Returns the unsubscribe.
 *
 * Exposed for `useTurnstile` and for tests; a screen reads the token through the hook.
 */
export function onTurnstileToken(listener: (next: string | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The sitekey, or '' when this build has none. Read once; it is a build constant. */
export function siteKey(): string {
  const key: unknown = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return typeof key === 'string' ? key.trim() : '';
}

/**
 * The token to send as `turnstile_token` on `POST /v1/auth/otp/request`.
 *
 * `null` has two causes and the caller cannot tell them apart, which is correct: this
 * build has no sitekey, or the widget has not answered yet. In both cases the honest
 * body is one with no `turnstile_token` field, and the Worker decides what that means
 * — it refuses when the control is deployed and lets it through when it is not. The
 * client never makes that decision, and never invents a token.
 */
export function turnstileToken(): string | null {
  return token;
}

/**
 * The token, for ONE request — and a fresh one asked for in its place.
 *
 * What every send reads. Reading `turnstileToken()` twice would send one token twice,
 * which Cloudflare refuses the second time (the header, § a token is spent once).
 * `null` means the same two things it means there, and the field is then omitted.
 */
export function takeTurnstileToken(): string | null {
  const taken = token;
  if (taken === null) return null;
  setToken(null);
  if (liveWidget !== null && typeof window !== 'undefined' && window.turnstile) {
    try {
      window.turnstile.reset(liveWidget);
    } catch {
      // A widget that cannot reset is one that will not issue again; the button stays
      // held, and the Worker would have refused a reused token in any case.
      console.warn('[turnstile] the widget could not be reset after its token was used');
    }
  }
  return taken;
}

/** Whether this build carries the challenge at all. */
export function turnstileRequired(): boolean {
  return siteKey() !== '';
}

let ready: Promise<void> | null = null;

/** Loads `api.js` once per page. Resolves when `window.turnstile` exists. */
function loadScript(): Promise<void> {
  if (ready) return ready;
  ready = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    window[READY_CALLBACK] = () => resolve();
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) return;
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    // A blocked or failed script must not leave a promise pending for the life of the
    // page: the screen has to keep working, and the Worker is what decides whether a
    // request with no token is acceptable.
    script.onerror = () => reject(new Error('Turnstile script did not load'));
    document.head.appendChild(script);
  });
  return ready;
}

/**
 * Renders the widget into `element` and keeps `turnstileToken()` current.
 *
 * Returns the teardown. Safe to call in a build with no sitekey — it does nothing at
 * all, loads no script, and returns a teardown that does nothing.
 */
export function mountTurnstile(element: HTMLElement): () => void {
  const key = siteKey();
  if (key === '') return () => {};

  let widgetId: string | null = null;
  let cancelled = false;

  void loadScript().then(
    () => {
      if (cancelled || !window.turnstile) return;
      widgetId = window.turnstile.render(element, {
        sitekey: key,
        // See the header: the design draws no widget, so it is drawn only if
        // Cloudflare actually needs the person to do something.
        appearance: 'interaction-only',
        // A token is valid for five minutes and this screen can be left open longer
        // than that while somebody finds their SIM.
        'refresh-expired': 'auto',
        callback: (t: string) => {
          setToken(t);
        },
        'expired-callback': () => {
          setToken(null);
        },
        'error-callback': () => {
          setToken(null);
        },
      });
      liveWidget = widgetId;
    },
    () => {
      // Recorded rather than silent. The screen stays usable in every way but the one
      // the Worker enforces; see `useTurnstile` for what the button does meanwhile.
      setToken(null);
      console.warn('[turnstile] the challenge script did not load; no token will be sent');
    },
  );

  return () => {
    cancelled = true;
    setToken(null);
    if (widgetId !== null && liveWidget === widgetId) liveWidget = null;
    if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId);
  };
}

/**
 * The challenge, for a screen that sends: mounts the widget into `container` while
 * `active`, and says whether the send button may be pressed.
 *
 * `ready` is true when this build has no sitekey (nothing will ever arrive, and nothing
 * is enforced) or when a token is in hand. `active` exists because a screen swaps its
 * body for a state frame, and the container goes with it: the widget is torn down with
 * it and mounted again when the screen comes back.
 */
export function useTurnstile(
  container: RefObject<HTMLElement>,
  active: boolean,
): { required: boolean; ready: boolean } {
  const [held, setHeld] = useState<string | null>(token);
  useEffect(() => {
    const unsubscribe = onTurnstileToken(setHeld);
    // A token that arrived between the first render and this subscription is not missed.
    setHeld(token);
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (!active) return undefined;
    const element = container.current;
    if (element === null) return undefined;
    return mountTurnstile(element);
  }, [active, container]);
  const required = turnstileRequired();
  return { required, ready: !required || held !== null };
}
